/**
 * CompanyDashboardAddAllObjectiveDrawer
 *
 * Guided creation flow for a new Objective with optional Key Results and Initiatives.
 * Composes header, sidebar, and main content sections and wires API calls for create
 * and update steps. Emits success/error alerts via callbacks supplied in props.
 */
import React, { useState } from "react";
import {
  addInitiative,
  addKeyresult,
  addObjective,
  getObjectivesById,
  updateObjective,
} from "../../../../api/objectives";
import CompanyDashboardAllObjectiveDrawerHeader from "./company-dashboard-all-objective-drawer-header/company-dashboard-all-objective-drawer-header";
import CompanyDashboardAllObjectiveDrawerSideBar from "./company-dashboard-all-objective-drawer-side-bar/company-dashboard-all-objective-drawer-side-bar";
import CompanyDashboardAllObjectiveDrawerMainContent from "./company-dashboard-all-objective-drawer-main-content/company-dashboard-all-objective-drawer-main-content";
import "./company-dashboard-all-objective-add-drawer.scss";
import {
  formatInitiativePayload,
  formatKeyResultPayloadData,
  formatKeyResultResponse,
  formatObjectivePayload,
  formatObjectiveResponse,
} from "../../company-dashboard-utils";

const CompanyDashboardAddAllObjectiveDrawer: any = (props: any) => {
  const {
    setIsShowAllObjectiveDrawer,
    setAlertMessage,
    setAlertType,
    setAlertOpen,
    setLoading,
    fetchObjectives,
    employeesList,
    statusListObjectives,
    statusListKeyResults,
    statusListInitiatives,
    departmentsList,
    fetchUpcomingMilestones,
    fetchAllYearsForObjectives,
    fetchAllYearsForInitiatives,
  } = props;
  const [listOfKeyResultsData, setListOfKeyResultsData] = useState<any[]>([]);
  const [newObjectiveData, setNewObjectiveData] = useState<any>(null);
  const [descriptionData, setDescriptionData] = useState<any>("");
  const [objectiveResponse, setObjectiveResponse] = useState<any>(null);
  const [keyResultData, setKeyResultData] = useState<any[]>([]); // all key results for this objective
  const [selectedKeyResult, setSelectedKeyResult] = useState<any>(null);

  const fetchObjectivesById = async (objectId: any, lastKeyResult?: any) => {
    setLoading(true);
    try {
      const response = await getObjectivesById({ objectId });
      const keyResults = response?.data?.data?.keyResults || [];
      const normalizedKeyResults = keyResults.map((kr: any) => ({
        ...kr,
        id: Number(kr.pk), // or Number(kr.id) depending on your format
        isSelected: Number(kr.pk) === Number(lastKeyResult?.id), // mark selected
      }));

      setListOfKeyResultsData(normalizedKeyResults);

      setNewObjectiveData(response?.data?.data);
      setDescriptionData(response?.data?.data?.objectiveDescription || "");
      setAlertMessage("Fetched objective successfully");
      setAlertType("success");
      setAlertOpen(true);
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setNewObjectiveData(null);
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch campaigns",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  const handleCreateObjective = async (newObjectiveData: any) => {
    setLoading(true);
    try {
      const payload = formatObjectivePayload(newObjectiveData);
      const response = await addObjective(payload);
      const formattedResponse = formatObjectiveResponse(response?.data?.data);
      setObjectiveResponse(formattedResponse[0]);
      setAlertMessage("Objective created successfully");
      setAlertType("success");
      setAlertOpen(true);
      setNewObjectiveData(formattedResponse[0]);
      fetchObjectives();
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to create objective",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const handleUpdateObjectiveDescription = async (description: any) => {
    setLoading(true);
    try {
      const response = await updateObjective(
        newObjectiveData.id ?? description.objectiveId,
        {
          objectiveDescription: description.description,
        },
      );

      setAlertMessage("Description added successfully");
      setAlertType("success");
      setDescriptionData(response?.data?.data?.objectiveDescription);
      setAlertOpen(true);
      fetchObjectives(); // refresh objectives
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to update description",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  const handleCreateObjectiveKeyresult = async (formValues: any) => {
    setLoading(true);
    try {
      const payload = formatKeyResultPayloadData(formValues);
      const response = await addKeyresult(payload); // <-- API call for key results
      setAlertMessage("Key Result created successfully");
      setAlertType("success");
      setAlertOpen(true);
      const formattedKeyResults = formatKeyResultResponse(response.data.data);
      const normalizedKeyResults = formattedKeyResults.map((kr: any) => ({
        ...kr,
        id: Number(kr.id),
      }));

      const lastKeyResult = normalizedKeyResults.at(-1);
      setSelectedKeyResult(normalizedKeyResults.at(-1));
      fetchObjectivesById(
        objectiveResponse?.pk ?? objectiveResponse?.id,
        lastKeyResult,
      );
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to update key result",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const handleCreateInitiative = async (formValues: any) => {
    setLoading(true);
    try {
      const payload = await formatInitiativePayload(
        formValues,
        objectiveResponse?.id, // or wherever your current objectiveId comes from
        selectedKeyResult?.id, // or wherever your current keyResultId comes from
      );

      await addInitiative(payload); // <-- API call
      setAlertMessage("Initiative created successfully");
      setAlertType("success");
      setAlertOpen(true);

      fetchObjectivesById(
        objectiveResponse?.pk ?? objectiveResponse?.id,
        selectedKeyResult,
      );
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to create Initiative",
      );
      setAlertType("error");
      setAlertOpen(true);
      fetchObjectivesById(objectiveResponse?.pk ?? objectiveResponse?.id);
      setLoading(false);
    }
  };
  return (
    <div className="company-dashboard-add-all-objective-drawer">
      <div className="company-dashboard-add-all-objective-drawer-content">
        <CompanyDashboardAllObjectiveDrawerHeader
          setIsShowAllObjectiveDrawer={setIsShowAllObjectiveDrawer}
          fetchObjectives={fetchObjectives}
          fetchUpcomingMilestones={fetchUpcomingMilestones}
          fetchAllYearsForObjectives={fetchAllYearsForObjectives}
          fetchAllYearsForInitiatives={fetchAllYearsForInitiatives}
        />
        <div className="company-dashboard-add-all-objective-drawer-content-body-section">
          <CompanyDashboardAllObjectiveDrawerSideBar
            newObjectiveData={newObjectiveData}
            setNewObjectiveData={setNewObjectiveData}
            descriptionData={descriptionData}
            setDescriptionData={setDescriptionData}
            keyResultData={keyResultData}
            setKeyResultData={setKeyResultData}
            selectedKeyResult={selectedKeyResult}
            setSelectedKeyResult={setSelectedKeyResult}
            employeesList={employeesList}
            listOfKeyResultsData={listOfKeyResultsData}
            setListOfKeyResultsData={setListOfKeyResultsData}
            saveObjective={handleCreateObjective}
            saveDescription={handleUpdateObjectiveDescription}
            saveKeyresult={handleCreateObjectiveKeyresult}
            statusListObjectives={statusListObjectives}
            statusListKeyResults={statusListKeyResults}
            departmentsList={departmentsList}
            setLoading={setLoading}
          />
          <CompanyDashboardAllObjectiveDrawerMainContent
            listOfKeyResultsData={listOfKeyResultsData}
            setListOfKeyResultsData={setListOfKeyResultsData}
            saveInitiative={handleCreateInitiative}
            employeesList={employeesList}
            statusListInitiatives={statusListInitiatives}
            departmentsList={departmentsList}
            statusListKeyResults={statusListKeyResults}
            statusListObjectives={statusListObjectives}
            setLoading={setLoading}
            objectId={objectiveResponse?.pk ?? objectiveResponse?.id}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}
            setAlertOpen={setAlertOpen}
            fetchObjectivesById={() =>
              fetchObjectivesById(
                objectiveResponse?.pk ?? objectiveResponse?.id,
                selectedKeyResult,
              )
            }
          />
        </div>
      </div>
      <div className="company-dashboard-add-all-objective-drawer-content-actions"></div>
    </div>
  );
};

export default CompanyDashboardAddAllObjectiveDrawer;
